import { useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { apiFetch } from '../utils/api';
import {
  removeLocalTrackLayers,
  savePersistentRescueData,
  loadPersistentRescueData,
  saveRescueTargetsToStorage,
  saveRescueTracksToStorage,
  TRACK_PREVIEW_ID
} from '../utils/rescuePush';
import { styleRescueTrackLineLayers } from '../utils/dashboard/rescueMapLayers';
import { filterActiveRescueItems } from '../utils/dashboard/rescueDataApi';

/**
 * 轨迹/目标叠加、预览与 localStorage 持久化
 */
export default function useRescuePushOverlays({
  mapRef,
  trackPreviewMarkersRef,
  trackLinesRef,
  rescueTargetMarkersRef,
  setTrackPreviewCoords,
  setTrackPreviewInfo,
  setTrackFile
}) {
  const [tracks, setTracks] = useState([]);
  const [rescueTargets, setRescueTargets] = useState([]);
  const [persistentTargets, setPersistentTargets] = useState([]);
  const [persistentTracks, setPersistentTracks] = useState([]);

  const savePersistentData = () => {
    savePersistentRescueData(persistentTargets, persistentTracks);
  };

  const loadPersistentData = () => {
    const { targets: loadedTargets, tracks: loadedTracks } = loadPersistentRescueData();
    if (loadedTargets.length) setPersistentTargets(loadedTargets);
    if (loadedTracks.length) setPersistentTracks(loadedTracks);
    return { targets: loadedTargets, tracks: loadedTracks };
  };

  const clearTrackPreview = () => {
    const map = mapRef.current;
    const previewId = TRACK_PREVIEW_ID;

    trackPreviewMarkersRef.current.forEach((marker) => {
      try {
        marker.remove();
      } catch (e) {
        /* ignore */
      }
    });
    trackPreviewMarkersRef.current = [];

    if (map) {
      try {
        if (map.getLayer(`${previewId}-line`)) map.removeLayer(`${previewId}-line`);
        if (map.getSource(previewId)) map.removeSource(previewId);
      } catch (e) {
        console.log('清除轨迹预览失败:', e);
      }
    }

    setTrackPreviewCoords(null);
    setTrackPreviewInfo('');
  };

  const showTrackPreview = (coordinates, fileName = '') => {
    if (!mapRef.current || !coordinates || coordinates.length < 2) {
      clearTrackPreview();
      setTrackPreviewInfo('文件点数不足，无法预览');
      return;
    }

    const map = mapRef.current;
    const previewId = TRACK_PREVIEW_ID;
    clearTrackPreview();

    const lineCoords = coordinates
      .map((c) => [Number(c.lng), Number(c.lat)])
      .filter(([lng, lat]) => Number.isFinite(lng) && Number.isFinite(lat));

    if (lineCoords.length < 2) {
      setTrackPreviewInfo('坐标无效，无法预览');
      return;
    }

    map.addSource(previewId, {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: { preview: true, fileName },
        geometry: { type: 'LineString', coordinates: lineCoords }
      }
    });

    map.addLayer({
      id: `${previewId}-line`,
      type: 'line',
      source: previewId,
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': '#1976d2',
        'line-width': 4,
        'line-opacity': 0.9,
        'line-dasharray': [2, 1.5]
      }
    });

    const ends = [
      { coord: lineCoords[0], label: '起点', color: '#2e7d32' },
      { coord: lineCoords[lineCoords.length - 1], label: '终点', color: '#c62828' }
    ];
    ends.forEach(({ coord, label, color }) => {
      const el = document.createElement('div');
      el.title = label;
      el.style.width = '12px';
      el.style.height = '12px';
      el.style.borderRadius = '50%';
      el.style.background = color;
      el.style.border = '2px solid #fff';
      el.style.boxShadow = '0 1px 4px rgba(0,0,0,0.35)';
      const marker = new mapboxgl.Marker(el).setLngLat(coord).addTo(map);
      trackPreviewMarkersRef.current.push(marker);
    });

    const bounds = new mapboxgl.LngLatBounds();
    lineCoords.forEach((c) => bounds.extend(c));
    map.fitBounds(bounds, { padding: 60, maxZoom: 16, duration: 800 });

    setTrackPreviewCoords(coordinates);
    setTrackPreviewInfo(`预览已加载：${coordinates.length} 个点（蓝色虚线，仅本页预览，未推送）`);
  };

  const clearAllTracks = () => {
    if (!mapRef.current) return;

    const map = mapRef.current;
    const mapAlive = (() => {
      try {
        return !map._removed && typeof map.getStyle === 'function' && !!map.getStyle();
      } catch (_) {
        return false;
      }
    })();

    trackLinesRef.current.forEach((entry) => {
      const trackId = typeof entry === 'string' ? entry : entry?.id;
      const markers = typeof entry === 'object' && Array.isArray(entry?.markers) ? entry.markers : [];
      markers.forEach((marker) => {
        try {
          marker?.remove?.();
        } catch (_) {
          /* ignore */
        }
      });
      try {
        if (mapAlive && trackId) removeLocalTrackLayers(map, trackId);
      } catch (error) {
        console.log('清理轨迹时出错:', error);
      }
    });

    trackLinesRef.current = [];
    setTracks([]);

    try {
      trackPreviewMarkersRef.current.forEach((marker) => {
        try {
          marker.remove();
        } catch (e) {
          /* ignore */
        }
      });
      trackPreviewMarkersRef.current = [];
      if (mapAlive) {
        if (map.getLayer('track-preview-line')) map.removeLayer('track-preview-line');
        if (map.getSource('track-preview')) map.removeSource('track-preview');
      }
    } catch (e) {
      console.log('清理预览轨迹时出错:', e);
    }
    setTrackPreviewCoords(null);
    setTrackPreviewInfo('');
    setTrackFile(null);
  };

  const clearAllTargets = () => {
    if (!mapRef.current) return;

    rescueTargetMarkersRef.current.forEach((markerRef) => {
      try {
        if (markerRef && markerRef.marker && markerRef.marker.remove) {
          markerRef.marker.remove();
        }
      } catch (error) {
        console.log('清理标记时出错:', error);
      }
    });

    rescueTargetMarkersRef.current = [];
    setRescueTargets([]);
  };

  const displayTrackOnMap = (trackData, options = {}) => {
    const fitView = options.fitView !== false;
    if (!mapRef.current || !trackData.coordinates || trackData.coordinates.length === 0) {
      return;
    }

    const map = mapRef.current;
    const trackId = `track-${trackData.id}`;

    try {
      removeLocalTrackLayers(map, trackId);
    } catch (error) {
      console.log('移除旧轨迹时出错:', error);
    }

    const coordinates = trackData.coordinates.map((coord) => [coord.lng, coord.lat]);

    map.addSource(trackId, {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {
          message: trackData.message,
          icon: trackData.icon
        },
        geometry: {
          type: 'LineString',
          coordinates
        }
      }
    });

    styleRescueTrackLineLayers(map, trackId, {
      withArrows: coordinates.length >= 2
    });

    const endpointMarkers = [];
    const ends = [
      { coord: coordinates[0], raw: trackData.coordinates[0], label: '起点', color: '#2e7d32', index: 0 },
      {
        coord: coordinates[coordinates.length - 1],
        raw: trackData.coordinates[trackData.coordinates.length - 1],
        label: '终点',
        color: '#c62828',
        index: trackData.coordinates.length - 1
      }
    ].filter((item, idx, arr) => item.coord && (arr.length === 1 || idx === 0 || item.index !== arr[0].index));

    ends.forEach(({ coord, raw, label, color, index }) => {
      const el = document.createElement('div');
      el.className = 'track-endpoint-marker';
      el.title = label;
      el.style.width = '12px';
      el.style.height = '12px';
      el.style.borderRadius = '50%';
      el.style.backgroundColor = color;
      el.style.border = '2px solid white';
      el.style.cursor = 'pointer';
      el.style.boxShadow = '0 1px 4px rgba(0,0,0,0.35)';
      const popup = new mapboxgl.Popup({ offset: 18 }).setHTML(`
            <div>
              <h4>${label}（点 ${index + 1}）</h4>
              <p>消息: ${trackData.message || ''}</p>
              <p>坐标: ${Number(raw?.lat).toFixed(6)}, ${Number(raw?.lng).toFixed(6)}</p>
              ${raw?.time ? `<p>时间: ${new Date(raw.time).toLocaleString()}</p>` : ''}
            </div>
          `);
      endpointMarkers.push(new mapboxgl.Marker(el).setLngLat(coord).setPopup(popup).addTo(map));
    });

    trackLinesRef.current.push({ id: trackId, markers: endpointMarkers });

    setPersistentTracks((prev) => {
      const existing = prev.find((t) => String(t.id) === String(trackData.id));
      if (!existing) {
        const newTracks = [...prev, trackData];
        setTimeout(() => {
          saveRescueTracksToStorage(newTracks);
        }, 0);
        return newTracks;
      }
      return prev;
    });

    if (fitView) {
      const bounds = new mapboxgl.LngLatBounds();
      coordinates.forEach((coord) => bounds.extend(coord));
      map.fitBounds(bounds, { padding: 50 });
    }
  };

  const displayTargetOnMap = (targetData) => {
    if (!mapRef.current) return;

    const map = mapRef.current;
    const markerId = `target-${targetData.id}`;

    const existingMarker = rescueTargetMarkersRef.current.find((m) => m.id === markerId);
    if (existingMarker) {
      try {
        if (existingMarker.marker && existingMarker.marker.remove) {
          existingMarker.marker.remove();
        }
      } catch (error) {
        console.log('移除旧标记时出错:', error);
      }
      rescueTargetMarkersRef.current = rescueTargetMarkersRef.current.filter((m) => m.id !== markerId);
    }

    const el = document.createElement('div');
    el.className = 'rescue-target-marker';
    el.style.width = '20px';
    el.style.height = '20px';
    el.style.borderRadius = '50%';
    el.style.backgroundColor = targetData.level === '紧急' ? '#ff0000' : '#ffaa00';
    el.style.border = '3px solid white';
    el.style.cursor = 'pointer';

    el.addEventListener('click', () => {
      const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(`
          <div>
            <h4>救援目标 #${targetData.id}</h4>
            <p>消息: ${targetData.message}</p>
            <p>级别: ${targetData.level}</p>
            <p>坐标: ${targetData.lat.toFixed(6)}, ${targetData.lng.toFixed(6)}</p>
            <button onclick="this.parentElement.parentElement.parentElement.remove()" style="margin-top: 8px; padding: 4px 8px; background: #f44336; color: white; border: none; border-radius: 4px; cursor: pointer;">删除</button>
          </div>
        `);

      marker.setPopup(popup);
    });

    const marker = new mapboxgl.Marker(el).setLngLat([targetData.lng, targetData.lat]).addTo(map);

    rescueTargetMarkersRef.current.push({
      id: markerId,
      marker,
      data: targetData
    });

    setPersistentTargets((prev) => {
      const existing = prev.find((t) => String(t.id) === String(targetData.id));
      if (!existing) {
        const newTargets = [...prev, targetData];
        setTimeout(() => {
          saveRescueTargetsToStorage(newTargets);
        }, 0);
        return newTargets;
      }
      return prev;
    });
  };

  const handleTrackReceived = (trackData) => {
    setTracks((prevTracks) => {
      const newTracks = [...prevTracks];
      const existingIndex = newTracks.findIndex((t) => String(t.id) === String(trackData.id));

      if (existingIndex !== -1) {
        newTracks[existingIndex] = trackData;
      } else {
        newTracks.push(trackData);
      }

      return newTracks;
    });

    displayTrackOnMap(trackData);
  };

  const handleTargetReceived = (targetData) => {
    setRescueTargets((prevTargets) => {
      const newTargets = [...prevTargets];
      const existingIndex = newTargets.findIndex((t) => String(t.id) === String(targetData.id));

      if (existingIndex !== -1) {
        newTargets[existingIndex] = targetData;
      } else {
        newTargets.push(targetData);
      }

      return newTargets;
    });

    displayTargetOnMap(targetData);
  };

  const loadFromHistory = async () => {
    try {
      console.log('按服务端活跃列表对账救援叠加（忽略已删/已完结的 localStorage 幽灵）...');

      const [targetResponse, trackResponse] = await Promise.all([
        apiFetch('/api/rescue/target'),
        apiFetch('/api/rescue/track')
      ]);
      const targetData = await targetResponse.json();
      const trackData = await trackResponse.json();

      const activeTargets =
        targetData.success && Array.isArray(targetData.targets)
          ? filterActiveRescueItems(targetData.targets)
          : [];
      const activeTracks =
        trackData.success && Array.isArray(trackData.tracks)
          ? filterActiveRescueItems(trackData.tracks)
          : [];

      // API 成功后再清本地叠加并回写缓存，避免网络失败时空图却留幽灵
      clearAllTracks();
      clearAllTargets();

      setPersistentTargets(activeTargets);
      setPersistentTracks(activeTracks);
      saveRescueTargetsToStorage(activeTargets);
      saveRescueTracksToStorage(activeTracks);

      activeTargets.forEach((target) => {
        displayTargetOnMap(target);
      });
      // 打开页面恢复时不要 fitBounds，避免飞向已删/过期轨迹位置
      activeTracks.forEach((track) => {
        displayTrackOnMap(track, { fitView: false });
      });

      console.log(
        '救援对账完成: 目标',
        activeTargets.length,
        '个, 轨迹',
        activeTracks.length,
        '条'
      );
    } catch (error) {
      console.error('从历史记录加载数据失败:', error);
    }
  };

  return {
    tracks,
    rescueTargets,
    persistentTargets,
    setPersistentTargets,
    persistentTracks,
    setPersistentTracks,
    savePersistentData,
    loadPersistentData,
    loadFromHistory,
    clearAllTracks,
    clearAllTargets,
    clearTrackPreview,
    showTrackPreview,
    displayTrackOnMap,
    displayTargetOnMap,
    handleTrackReceived,
    handleTargetReceived
  };
}
